import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const BTN = "px-3 py-1.5 text-xs border rounded-sm disabled:opacity-50";
const STAGES = [
  { stage: "archive", label: "Run archive", cls: "border-amber-700 text-amber-300 hover:border-amber-400" },
  { stage: "purge", label: "Run purge", cls: "border-red-700 text-red-300 hover:border-red-400" },
  { stage: "all", label: "Run all stages", cls: "border-emerald-700 text-emerald-300 hover:border-emerald-400" },
];

function summarize(job) {
  if (job.status === "failed") return `Failed: ${job.error || "unknown error"}`;
  const r = job.result || {};
  const a = r.archive || r, p = r.purge || r, t = r.tombstones || r;
  const parts = [`Archived ${a.archived ?? 0}`, `Purged ${p.purged ?? 0}`];
  if (t.deleted != null) parts.push(`Tombstones deleted ${t.deleted}`);
  return `${parts.join(" · ")} (ran in the background worker)`;
}

/** Settings → Article lifecycle → Manual run. Real runs are queued for the worker process and polled. */
export default function LifecycleRunButtons({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);

  function poll(id) {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/admin/lifecycle/jobs/${id}`);
        setJob(r.data);
        if (!["queued", "running"].includes(r.data.status)) {
          clearInterval(timer.current); setBusy(false); setMsg(summarize(r.data)); onDone && onDone();
        }
      } catch (e) { clearInterval(timer.current); setBusy(false); setMsg(e.response?.data?.detail || e.message); }
    }, 2000);
  }

  async function dryRun() {
    setBusy(true); setMsg(""); setJob(null);
    try {
      const r = await api.post("/admin/lifecycle/dry-run?stage=all");
      setMsg(`Dry-run: would archive ${r.data.archive.archived} · would purge ${r.data.purge.purged}`);
    } catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  async function run(stage) {
    setBusy(true); setMsg(""); setJob(null);
    try {
      const r = await api.post(`/admin/lifecycle/run?stage=${stage}`);
      setJob({ id: r.data.job_id, status: "queued", stage });
      poll(r.data.job_id);
    } catch (e) { setMsg(e.response?.data?.detail || "Failed"); setBusy(false); }
  }

  const live = job && ["queued", "running"].includes(job.status);
  return (
    <div className="flex gap-2 items-center flex-wrap">
      <button data-testid="lifecycle-dry-run" disabled={busy} onClick={dryRun} className={`${BTN} border-zinc-700 hover:border-zinc-400`}>Dry-run</button>
      {STAGES.map((s) => (
        <button key={s.stage} data-testid={`lifecycle-run-${s.stage}`} disabled={busy} onClick={() => run(s.stage)} className={`${BTN} ${s.cls}`}>{s.label}</button>
      ))}
      {live && (
        <span data-testid="lifecycle-run-job" className="text-xs text-amber-300 mono flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> {job.status === "queued" ? "queued for the background worker…" : `running ${job.stage || ""} in the worker…`}
        </span>
      )}
      {msg && <span data-testid="lifecycle-run-msg" className="text-xs text-zinc-400 mono">{msg}</span>}
    </div>
  );
}
